"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  SelectInput,
  ResultBanner,
  PrintButton,
  DataReviewedNote,
  PrintFooter,
  trackToolResult,
} from "@/components/tools/ui";

/* CSCS card decision logic.
   VERIFIED June 2026 against the official scheme website (cscs.uk.com):
   - Card types & who they're for: cscs.uk.com/types-of-cscs-cards
   - Green Labourer: L1 (Scotland L4) Award in H&S in a Construction Environment
     + Operatives HS&E test; first card valid 2 years, renewals 5 years.
   - Blue Skilled Worker: construction-related NVQ/SVQ L2 (or completed
     apprenticeship) + appropriate HS&E test; valid 5 years.
   - Gold Advanced Craft / Skilled Worker: NVQ/SVQ L3 + appropriate HS&E test;
     valid 5 years.
   - Gold Supervisor: NVQ/SVQ L3 or L4 in a supervisory occupation + a Specialist
     (Supervisory) HS&E test; valid 5 years.
   - Black Manager: NVQ/SVQ L4, 5, 6 or 7 in construction management + the
     Managers & Professionals HS&E test; valid 5 years.
   - AQP/PQP (professionals): construction-related degree/HNC/HND or membership of
     a CSCS-approved professional body + Managers & Professionals HS&E test;
     valid 5 years.
   - Red cards are temporary, on training pathways: Apprentice 4y6m (no renewal),
     Trainee 5y (no renewal), Experienced Worker 1y (no renewal), Provisional/
     Experienced Technical/Supervisor/Manager are temporary registration routes.
   - Industry Accreditation ("grandfather rights") was withdrawn: renewals closed
     30 Jun 2024, all IA cards expired 31 Dec 2024 — every card must now be linked
     to a qualification or professional-body membership.
   Where the route is genuinely case-by-case (specialist occupations, partner
   schemes, professional cards) we point at the official CSCS Card Finder rather
   than guessing. */

type RoleId =
  | "labourer"
  | "skilled"
  | "apprentice"
  | "supervisor"
  | "manager"
  | "professional";

type QualId =
  | "none"
  | "l1"
  | "l2"
  | "l3"
  | "l4to7"
  | "degree"
  | "registered";

type TestId = "none" | "operatives" | "specialist" | "managers";

const ROLES: { id: RoleId; label: string }[] = [
  { id: "labourer", label: "Labourer / general operative" },
  { id: "skilled", label: "Skilled trade (bricklayer, carpenter, etc.)" },
  { id: "apprentice", label: "Apprentice or trainee" },
  { id: "supervisor", label: "Supervisor" },
  { id: "manager", label: "Manager" },
  { id: "professional", label: "Visiting professional (designer, QS, engineer)" },
];

const QUALS: { id: QualId; label: string }[] = [
  { id: "none", label: "None / not yet qualified" },
  { id: "registered", label: "Registered on a qualification (working towards it)" },
  { id: "l1", label: "Level 1 Award in H&S in a Construction Environment" },
  { id: "l2", label: "NVQ/SVQ Level 2 (or completed apprenticeship)" },
  { id: "l3", label: "NVQ/SVQ Level 3" },
  { id: "l4to7", label: "NVQ/SVQ Level 4–7 (construction management)" },
  { id: "degree", label: "Degree / professional-body membership" },
];

const TESTS: { id: TestId; label: string }[] = [
  { id: "none", label: "No — not taken (or expired)" },
  { id: "operatives", label: "Yes — Operatives HS&E test" },
  { id: "specialist", label: "Yes — Specialist (e.g. Supervisory) HS&E test" },
  { id: "managers", label: "Yes — Managers & Professionals (MAP) HS&E test" },
];

/* The test level CSCS expects for the card the role points at. */
const TEST_FOR_ROLE: Record<RoleId, TestId> = {
  labourer: "operatives",
  skilled: "operatives",
  apprentice: "operatives",
  supervisor: "specialist",
  manager: "managers",
  professional: "managers",
};

const TEST_LABEL: Record<TestId, string> = {
  none: "none",
  operatives: "Operatives HS&E test",
  specialist: "Specialist (Supervisory) HS&E test",
  managers: "Managers & Professionals (MAP) HS&E test",
};

interface Outcome {
  /** The card name we show as the headline. */
  card: string;
  /** One-line who-it's-for / why this card. */
  summary: string;
  /** What the card requires — checklist rows (label + whether they have it). */
  requires: { label: string; have: boolean | null }[];
  validity: string;
  /** Next steps to apply. */
  steps: string[];
  /** True when we couldn't pin a single card and defer to the official finder. */
  defer?: boolean;
  /** Optional partner-scheme note. */
  partner?: string;
}

const FINDER_URL = "https://www.cscs.uk.com/card-finder/";

function decide(role: RoleId | "", qual: QualId | "", test: TestId | ""): Outcome | null {
  if (!role || !qual || !test) return null;

  const needTest = TEST_FOR_ROLE[role];
  const hasRightTest = test === needTest;
  const partner =
    "If you operate plant, work in electrotechnical/electrical, or work as a registered plumber/gas engineer, you may need a partner-scheme card instead of (or as well as) a CSCS card — CPCS or NPORS for plant, ECS for electrical/electrotechnical, and the JIB/SNIPEF route for plumbing. The role-specific card carries your CSCS logo; check the CSCS Card Finder for the right scheme.";

  // ── Visiting professionals ──────────────────────────────────────────────
  if (role === "professional") {
    if (qual === "degree") {
      return {
        card: "AQP / PQP — Professional card",
        summary:
          "Academically Qualified Person (AQP) or Professionally Qualified Person (PQP) — for site professionals with a construction-related qualification or professional-body membership.",
        requires: [
          { label: "A construction-related degree, HNC/HND — or membership of a CSCS-approved professional body", have: true },
          { label: `Pass the ${TEST_LABEL.managers} within the last two years`, have: hasRightTest },
        ],
        validity: "Valid for 5 years.",
        steps: [
          "Confirm your qualification or professional body is CSCS-approved (the approved-body list is on cscs.uk.com).",
          hasRightTest
            ? "You have the right test — apply for the AQP/PQP card through your professional body or CSCS."
            : "Book and pass the Managers & Professionals (MAP) HS&E test, then apply.",
        ],
        defer: false,
      };
    }
    return {
      card: "Professional card — route depends on your body",
      summary:
        "Professional routes (AQP/PQP) depend on your specific qualification or professional-body membership, so CSCS handles them case by case.",
      requires: [
        { label: "A construction-related degree/HNC/HND or CSCS-approved professional-body membership", have: false },
        { label: `Pass the ${TEST_LABEL.managers}`, have: hasRightTest },
      ],
      validity: "Professional (AQP/PQP) cards are typically valid for 5 years.",
      steps: [
        "Use the official CSCS Card Finder to confirm which professional route applies to you.",
        "Many professional bodies issue the CSCS-logo card directly to their members.",
      ],
      defer: true,
    };
  }

  // ── Apprentices / trainees ──────────────────────────────────────────────
  if (role === "apprentice") {
    if (qual === "registered" || qual === "none") {
      return {
        card: "Red Apprentice or Red Trainee card",
        summary:
          "Temporary cards for people on a recognised training pathway. The Apprentice card is for those on a recognised apprenticeship; the Trainee card is for those registered on (and working towards) a qualification such as an NVQ/SVQ, HNC, HND or degree.",
        requires: [
          { label: "On a recognised apprenticeship, or registered on a competence-assessed qualification", have: qual === "registered" },
          { label: `Pass the ${TEST_LABEL.operatives} (level appropriate to your route)`, have: test !== "none" },
        ],
        validity:
          "Apprentice card: valid 4 years 6 months (cannot be renewed). 5-year Trainee card: valid 5 years (cannot be renewed). You're expected to finish your qualification and move to a skilled card before it expires.",
        steps: [
          "Apprentices: ask your training provider — the Apprentice card is free and they usually apply for you.",
          "Trainees: register on your qualification first, then apply for the Trainee card.",
          test === "none"
            ? "Book and pass the appropriate Operatives HS&E test."
            : "You already hold an HS&E pass — keep it valid.",
        ],
        partner,
      };
    }
    // Apprentice who has now achieved a real qualification — route to skilled.
    return {
      card: qual === "l2" ? "Blue Skilled Worker card" : "Gold Advanced Craft card",
      summary:
        "You've completed your training and hold a recognised qualification, so you can move off the temporary trainee card onto a skilled card.",
      requires: [
        { label: qual === "l2" ? "Construction-related NVQ/SVQ Level 2 (or completed apprenticeship)" : "Construction-related NVQ/SVQ Level 3", have: true },
        { label: `Pass the ${TEST_LABEL.operatives} within the last two years (at the level for your occupation)`, have: test !== "none" },
      ],
      validity: "Valid for 5 years.",
      steps: [
        "Apply for the skilled card once your qualification is awarded.",
        test === "none" ? "Book and pass the appropriate HS&E test first." : "Keep your HS&E pass current.",
      ],
      partner,
    };
  }

  // ── Labourers ───────────────────────────────────────────────────────────
  if (role === "labourer") {
    const haveL1 = qual === "l1";
    return {
      card: "Green Labourer card",
      summary:
        "For workers carrying out labouring/general duties only — not for skilled trades. (If you actually work a skilled trade, pick that role above instead, as the Labourer card must not be used for skilled work.)",
      requires: [
        { label: "Level 1 (Scotland Level 4) Award in Health and Safety in a Construction Environment, or an approved alternative", have: haveL1 },
        { label: `Pass the ${TEST_LABEL.operatives}`, have: test === "operatives" },
      ],
      validity: "First Labourer card is valid for 2 years; on renewal it can be extended for a further 5 years.",
      steps: [
        haveL1
          ? "You hold the L1 Award — apply for the Green Labourer card."
          : "Complete the Level 1 Award in H&S in a Construction Environment (a short course).",
        test === "operatives" ? "Your Operatives HS&E pass is in place." : "Book and pass the Operatives HS&E test.",
        "Apply through cscs.uk.com once both are in place.",
      ],
      partner,
    };
  }

  // ── Skilled trades ──────────────────────────────────────────────────────
  if (role === "skilled") {
    if (qual === "l2") {
      return {
        card: "Blue Skilled Worker card",
        summary: "For skilled workers with a construction-related NVQ/SVQ Level 2 or a completed apprenticeship.",
        requires: [
          { label: "Construction-related NVQ/SVQ Level 2 (or completed apprenticeship)", have: true },
          { label: `Pass the appropriate HS&E test within the last two years (usually the ${TEST_LABEL.operatives})`, have: test !== "none" },
        ],
        validity: "Valid for 5 years.",
        steps: [
          "Apply for the Blue Skilled Worker card with your qualification certificate.",
          test === "none" ? "Book and pass the appropriate HS&E test first." : "Keep your HS&E pass current.",
        ],
        partner,
      };
    }
    if (qual === "l3") {
      return {
        card: "Gold Advanced Craft card",
        summary: "For skilled workers who hold a construction-related NVQ/SVQ Level 3 (advanced craft).",
        requires: [
          { label: "Construction-related NVQ/SVQ Level 3", have: true },
          { label: `Pass the appropriate HS&E test within the last two years (usually the ${TEST_LABEL.operatives})`, have: test !== "none" },
        ],
        validity: "Valid for 5 years.",
        steps: [
          "Apply for the Gold Advanced Craft card with your Level 3 certificate.",
          test === "none" ? "Book and pass the appropriate HS&E test first." : "Keep your HS&E pass current.",
        ],
        partner,
      };
    }
    if (qual === "registered") {
      return {
        card: "Red Trainee card (then a skilled card)",
        summary:
          "You're working in a skilled trade but registered on — not yet awarded — the qualification, so the temporary Trainee card bridges you until you qualify.",
        requires: [
          { label: "Registered on, and working towards, a competence-assessed qualification (NVQ/SVQ)", have: true },
          { label: `Pass the ${TEST_LABEL.operatives}`, have: test !== "none" },
        ],
        validity: "5-year Trainee card, cannot be renewed — move to a Blue (L2) or Gold (L3) skilled card once qualified.",
        steps: [
          "Apply for the Trainee card while you complete your NVQ/SVQ.",
          "Once awarded, apply for the Blue Skilled Worker (L2) or Gold Advanced Craft (L3) card.",
        ],
        partner,
      };
    }
    // Skilled trade with no qualification / only L1.
    return {
      card: "Red Experienced Worker card — or get qualified",
      summary:
        "You work a skilled trade but don't yet hold a relevant NVQ/SVQ. The Red Experienced Worker card is a one-off short-term route while you register for a qualification; the long-term card is Blue (L2) or Gold (L3).",
      requires: [
        { label: "On-the-job experience in a skilled occupation", have: true },
        { label: "A construction-related NVQ/SVQ (Level 2 for Blue, Level 3 for Gold) — needed for the permanent card", have: false },
        { label: `Pass the ${TEST_LABEL.operatives}`, have: test !== "none" },
      ],
      validity: "Red Experienced Worker card: valid 1 year and cannot be renewed — use the year to register for and complete an NVQ/SVQ.",
      steps: [
        "Register for the NVQ/SVQ that matches your trade (an on-site assessment route is usually available for experienced workers).",
        "Apply for the Red Experienced Worker card to keep working while you qualify.",
        "Once the qualification is awarded, move to the Blue (L2) or Gold (L3) skilled card.",
      ],
      partner,
    };
  }

  // ── Supervisors ─────────────────────────────────────────────────────────
  if (role === "supervisor") {
    if (qual === "l3" || qual === "l4to7") {
      return {
        card: "Gold Supervisor card",
        summary: "For supervisory and technical occupations with a relevant NVQ/SVQ at Level 3 or 4.",
        requires: [
          { label: "NVQ/SVQ Level 3 or 4 in a relevant supervisory occupation", have: true },
          { label: `Pass a ${TEST_LABEL.specialist}`, have: test === "specialist" },
        ],
        validity: "Valid for 5 years.",
        steps: [
          "Apply for the Gold Supervisor card with your supervisory NVQ/SVQ.",
          test === "specialist"
            ? "Your Specialist HS&E pass is in place."
            : "Book and pass the relevant Specialist (Supervisory) HS&E test — the Operatives test is not enough for this card.",
        ],
        partner,
      };
    }
    return {
      card: "Red Experienced Technical, Supervisor or Manager card",
      summary:
        "A temporary registration route for supervisors with on-the-job experience who don't yet hold the supervisory NVQ/SVQ needed for the Gold Supervisor card.",
      requires: [
        { label: "Supervisory experience on site", have: true },
        { label: "NVQ/SVQ Level 3 or 4 in a supervisory occupation — needed for the permanent Gold Supervisor card", have: false },
        { label: `Pass a ${TEST_LABEL.specialist}`, have: test === "specialist" },
      ],
      validity: "Temporary card while you register for and complete the supervisory qualification, then move to the Gold Supervisor card.",
      steps: [
        "Use the official CSCS Card Finder to confirm your eligibility for the Experienced route.",
        "Register for a supervisory NVQ/SVQ (Level 3/4), then apply for the Gold Supervisor card.",
      ],
      defer: true,
      partner,
    };
  }

  // ── Managers ────────────────────────────────────────────────────────────
  if (role === "manager") {
    if (qual === "l4to7" || qual === "degree") {
      return {
        card: "Black Manager card",
        summary: "For managerial and technical occupations with a construction-management NVQ/SVQ at Level 4, 5, 6 or 7.",
        requires: [
          { label: "Construction-management NVQ/SVQ Level 4, 5, 6 or 7", have: qual === "l4to7" || qual === "degree" },
          { label: `Pass the ${TEST_LABEL.managers}`, have: test === "managers" },
        ],
        validity: "Valid for 5 years.",
        steps: [
          "Apply for the Black Manager card with your management NVQ/SVQ certificate.",
          test === "managers"
            ? "Your Managers & Professionals HS&E pass is in place."
            : "Book and pass the Managers & Professionals (MAP) HS&E test — the Operatives test is not accepted for this card.",
        ],
        partner,
      };
    }
    return {
      card: "Red Experienced Technical, Supervisor or Manager card",
      summary:
        "A temporary registration route for managers with on-the-job experience who don't yet hold the Level 4–7 construction-management NVQ/SVQ the Black Manager card needs.",
      requires: [
        { label: "Management experience on site", have: true },
        { label: "Construction-management NVQ/SVQ Level 4–7 — needed for the permanent Black Manager card", have: false },
        { label: `Pass the ${TEST_LABEL.managers}`, have: test === "managers" },
      ],
      validity: "Temporary card while you register for and complete the qualification, then move to the Black Manager card.",
      steps: [
        "Use the official CSCS Card Finder to confirm your eligibility for the Experienced route.",
        "Register for a construction-management NVQ/SVQ (Level 4–7), then apply for the Black Manager card.",
      ],
      defer: true,
      partner,
    };
  }

  return null;
}

export function CscsCardFinder() {
  const [role, setRole] = useState<RoleId | "">("");
  const [qual, setQual] = useState<QualId | "">("");
  const [test, setTest] = useState<TestId | "">("");
  const [name, setName] = useState("");
  const resultTracked = useRef(false);

  const outcome = useMemo(() => decide(role, qual, test), [role, qual, test]);

  useEffect(() => {
    if (outcome && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("cscs-card-finder", { card: outcome.card });
    }
  }, [outcome]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
      {/* Inputs */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Tell us about your role</h2>
        <p className="mt-1 text-sm text-muted">
          Three quick questions. CSCS sets the rules and they change, so we always point you at the official card
          finder to confirm before you apply.
        </p>

        <div className="mt-5 space-y-4">
          <Field label="What is your role on site?">
            <SelectInput value={role} onChange={(e) => setRole(e.target.value as RoleId | "")}>
              <option value="">Choose your role…</option>
              {ROLES.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </SelectInput>
          </Field>

          <Field label="Highest relevant qualification you hold (or are working towards)">
            <SelectInput value={qual} onChange={(e) => setQual(e.target.value as QualId | "")}>
              <option value="">Choose your qualification…</option>
              {QUALS.map((q) => (
                <option key={q.id} value={q.id}>{q.label}</option>
              ))}
            </SelectInput>
          </Field>

          <Field
            label="Have you passed a current CITB Health, Safety & Environment (HS&E) test?"
            hint="Your pass must usually be within the last two years, and at the level for the card you want."
          >
            <SelectInput value={test} onChange={(e) => setTest(e.target.value as TestId | "")}>
              <option value="">Choose…</option>
              {TESTS.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </SelectInput>
          </Field>

          <Field label="" className="pt-1">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (for the printed summary)"
              className="w-full rounded-lg border border-line-2 bg-white px-3.5 py-2.5 text-[0.95rem] text-ink placeholder:text-muted/70 focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald/25"
            />
          </Field>
        </div>

        <p className="mt-5 rounded-lg bg-mist/70 px-4 py-3 text-[0.82rem] leading-relaxed text-muted">
          <strong className="text-ink">Industry Accreditation has gone.</strong> The old &ldquo;grandfather rights&rdquo;
          route closed for renewals on 30 June 2024 and all those cards expired on 31 December 2024. Every CSCS card
          now has to be backed by a recognised qualification or professional-body membership.
        </p>

        <p className="mt-3 rounded-lg border border-risk-high/40 bg-[#fef0e6] px-4 py-3 text-[0.82rem] leading-relaxed text-[#b34e10]">
          <strong>Apply through cscs.uk.com or an official partner scheme only.</strong> Third-party
          &ldquo;fast-track&rdquo; card sites charge a fee for something you can do yourself at the official price —
          they add nothing and some are scams. Only cscs.uk.com (and approved partner schemes such as CPCS/NPORS,
          ECS and JIB/SNIPEF) can issue a genuine card.
        </p>
      </div>

      {/* Result + printable summary */}
      <div className="space-y-4">
        {outcome ? (
          <>
            <div className="print-area card shadow-soft p-6" id="cscs-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                CSCS card guide
              </div>
              <h2 className="mt-1 text-lg">
                {name ? `${name} — ` : ""}
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </h2>

              <div className="mt-4">
                <ResultBanner level="low" title={outcome.card}>
                  {outcome.summary}
                </ResultBanner>
              </div>

              <div className="mt-4">
                <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">What it requires</div>
                <ul className="mt-2 space-y-2 text-sm">
                  {outcome.requires.map((r, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span
                        className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[0.65rem] font-bold ${
                          r.have === true
                            ? "bg-emerald-tint text-emerald-deep"
                            : r.have === false
                            ? "bg-[#fef0e6] text-[#b34e10]"
                            : "bg-mist text-muted"
                        }`}
                        aria-hidden
                      >
                        {r.have === true ? "✓" : r.have === false ? "!" : "•"}
                      </span>
                      <span className="leading-snug text-body">
                        {r.label}
                        {r.have === false && <span className="text-muted"> — not in place yet</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 rounded-lg bg-mist px-3.5 py-3 text-sm">
                <span className="font-semibold text-ink">Validity: </span>
                <span className="text-body">{outcome.validity}</span>
              </div>

              <div className="mt-4">
                <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">Next steps to apply</div>
                <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-body">
                  {outcome.steps.map((s, i) => (
                    <li key={i} className="leading-snug">{s}</li>
                  ))}
                </ol>
              </div>

              {outcome.partner && (
                <p className="mt-4 rounded-lg border border-line bg-white px-3.5 py-3 text-[0.82rem] leading-relaxed text-muted">
                  {outcome.partner}
                </p>
              )}

              <p className="mt-4 text-[0.82rem] leading-relaxed text-muted">
                {outcome.defer
                  ? "Your route is case-by-case, so confirm it with the "
                  : "Always confirm the current requirement before you apply on the "}
                <a href={FINDER_URL} rel="noopener" target="_blank" className="font-semibold text-emerald-deep underline">
                  official CSCS Card Finder
                </a>
                . CSCS is the authority on which card you need.
              </p>

              <p className="mt-4 rounded-lg border border-risk-high/40 bg-[#fef0e6] px-3.5 py-3 text-[0.82rem] leading-relaxed text-[#b34e10]">
                <strong>Apply through the official route only.</strong> Use cscs.uk.com or an approved partner
                scheme — third-party &ldquo;fast-track&rdquo; sites charge for nothing and cannot issue a genuine card.
              </p>

              <DataReviewedNote date="June 2026">
                Card types, routes and requirements verified against the official scheme (cscs.uk.com).
                The scheme changes — legacy cards are being withdrawn — so confirm before applying.
              </DataReviewedNote>

              <PrintFooter toolName="CSCS card finder" />
            </div>
            <div className="flex justify-end">
              <PrintButton tool="cscs-card-finder" label="Print / save summary as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Answer the three questions and we&apos;ll show the CSCS card that fits, what it requires, how long it lasts
            and how to apply.
          </div>
        )}
      </div>
    </div>
  );
}
